import { Dispatch } from 'redux';
import { v4 as uuidv4 } from 'uuid';
import { axiosInstance, request, requestConfig } from './base';
import { ResultadoKri } from 'common/genericTypes';
import { startLoading, endLoading } from 'store/modules/loader';

const KRI_BASE_PATH = 'api/v1/motor-riscos/instrumentos';

// A chamada de retry encadeia Motor de Cálculo (2 rotas), Curva DI e Motor de Riscos.
// Em teste integrado levou ~20s; o guia manda timeout mínimo de 90s.
const RETRY_TIMEOUT_MS = 90000;

const buildCorrelationHeader = (): Record<string, string> => ({
  'X-Correlation-Id': uuidv4(),
});

/**
 * Consulta o KRI de um instrumento.
 *
 * - 200: retorna o ResultadoKri.
 * - 204: retorna null (instrumento existe mas nunca foi avaliado).
 * - 404/502: throw. O wrapper padrão do projeto já dispara o snackbar de erro;
 *   o componente pode capturar via `error.response.status` para exibir estado próprio.
 */
export const GETKriMotorRiscos = async (
  dispatch: Dispatch,
  instrumentoFinanceiroGuid: string,
  stackRequest: boolean = false
): Promise<ResultadoKri | null> => {
  const result = await request<ResultadoKri | ''>(
    () =>
      axiosInstance.get<ResultadoKri | ''>(`${requestConfig.api}${KRI_BASE_PATH}/${instrumentoFinanceiroGuid}/kri`, {
        headers: buildCorrelationHeader(),
      }),
    dispatch,
    undefined,
    stackRequest
  );

  // 204 chega como '' (ou undefined). Só é ResultadoKri quando vem um objeto com chaveComposta.
  if (result && typeof result === 'object' && 'chaveComposta' in result) {
    return result as ResultadoKri;
  }
  return null;
};

/**
 * Força a reavaliação do KRI. Síncrona e pesada (usar timeout de 90s).
 *
 * Não passamos pelo wrapper `request` padrão porque queremos UX customizado por
 * status HTTP no componente (409 como info, 400 com etapaFalha detalhada etc.).
 * O snackbar é decidido no componente com base no erro.
 *
 * - 201: sucesso (corpo vazio). O componente deve refazer o GET para atualizar a tela.
 * - 400/409/502: throw AxiosError. Inspecione `error.response.status` e
 *   `error.response.data` (formato ErroApiMotorRiscos).
 */
export const POSTRetryKriMotorRiscos = async (
  dispatch: Dispatch,
  instrumentoFinanceiroGuid: string,
  stackRequest: boolean = false
): Promise<void> => {
  const requestId = uuidv4();
  if (stackRequest) dispatch(startLoading(requestId));

  try {
    await axiosInstance.post<void>(
      `${requestConfig.api}${KRI_BASE_PATH}/${instrumentoFinanceiroGuid}/reprocessamento`,
      undefined,
      {
        headers: buildCorrelationHeader(),
        timeout: RETRY_TIMEOUT_MS,
      }
    );
  } finally {
    if (stackRequest) dispatch(endLoading(requestId));
  }
};
